import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Building2, Search, X, MapPin, UserCheck } from 'lucide-react';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { TableRoot, Table, THead, TR, TH, TBody, TD } from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { Office, Controller, Region } from '../../lib/sdk/index';
import { toast } from 'react-toastify';
import { formatDate } from '../../lib/utils/formatting';
import { useOffices, useControllers, useRegions, useCreateOffice, useUpdateOffice, useDeleteOffice } from '../../hooks/useQueries';
import { getErrorMessage } from '../../lib/utils/errorMessage';

const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedValue(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

const ManageOffices: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 350);
  const limit = 50;
  
  // Use React Query hooks with pagination
  const { data: officesResponse, isLoading: officesLoading, refetch: refetchOffices } = useOffices({ 
    page, 
    limit, 
    search: debouncedSearchTerm || undefined 
  });
  const { data: controllersResponse = [] } = useControllers();
  const { data: regionsResponse = [] } = useRegions();
  
  const offices = Array.isArray(officesResponse?.data) ? officesResponse.data : [];
  const pagination = officesResponse?.pagination;
  const controllers = Array.isArray(controllersResponse?.data) ? controllersResponse.data : (Array.isArray(controllersResponse) ? controllersResponse : []);
  const regions = Array.isArray(regionsResponse?.data) ? regionsResponse.data : (Array.isArray(regionsResponse) ? regionsResponse : []);
  const loading = officesLoading;
  
  // Reset to page 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);
  
  const [showForm, setShowForm] = useState(false);
  const [editingOffice, setEditingOffice] = useState<Office | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteOfficeId, setDeleteOfficeId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    controllerId: '',
    regionId: ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [controllerSearchTerm, setControllerSearchTerm] = useState('');
  const [showControllerDropdown, setShowControllerDropdown] = useState(false);
  const [showCentralOfficeInfo, setShowCentralOfficeInfo] = useState(false);

  // Mutations
  const createOfficeMutation = useCreateOffice({
    onSuccess: () => {
      toast.success('Office created successfully!');
      setShowForm(false);
      setFormData({ name: '', controllerId: '', regionId: '' });
      setErrors({});
      refetchOffices();
    },
    onError: (error: any) => {
      console.error('Error creating office:', error);
      toast.error(getErrorMessage(error, 'Failed to create office'));
    }
  });

  const updateOfficeMutation = useUpdateOffice({
    onSuccess: () => {
      toast.success('Office updated successfully!');
      setShowForm(false);
      setEditingOffice(null);
      setFormData({ name: '', controllerId: '', regionId: '' });
      setErrors({});
      refetchOffices();
    },
    onError: (error: any) => {
      console.error('Error updating office:', error);
      toast.error(getErrorMessage(error, 'Failed to update office'));
    }
  });

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showControllerDropdown) {
        setShowControllerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showControllerDropdown]);


  // Get next available office number for a specific region
  const getNextOfficeNumber = (regionId?: string): number => {
    if (!regionId) {
      // For central office, always return 1
      return 1;
    }
    
    // Find the region to get its office range
    const region = regions.find(r => r._id === regionId);
    if (!region || !region.officeRange) {
      return 1;
    }
    
    // Get all offices in this region
    const regionOffices = offices.filter(o => 
      o.regionId && (typeof o.regionId === 'string' ? o.regionId === regionId : o.regionId._id === regionId)
    );
    
    if (regionOffices.length === 0) {
      // First office in this region
      return region.officeRange.start;
    }
    
    // Find the next available number in the sequence
    const usedNumbers = regionOffices.map(o => o.officeNo).sort((a, b) => a - b);
    let nextNumber = region.officeRange.start;
    
    for (const usedNum of usedNumbers) {
      if (usedNum === nextNumber) {
        nextNumber++;
      } else {
        break;
      }
    }
    
    // Ensure we don't exceed the region's range
    if (nextNumber > region.officeRange.end) {
      throw new Error(`No more office numbers available in this region (range: ${region.officeRange.start}-${region.officeRange.end})`);
    }
    
    return nextNumber;
  };

  // Validation functions
  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Office name is required';
    }

    if (!formData.controllerId.trim()) {
      newErrors.controllerId = 'Controller is required';
    }

    // For central office (office 1), region is not required
    // For all other offices, region is required
    if (!formData.regionId.trim() && !editingOffice?.isCentral) {
      newErrors.regionId = 'Region is required for regional offices';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    try {
      let submitData;
      
      if (editingOffice) {
        // For editing, keep the existing office number
        submitData = {
          name: formData.name.trim(),
          controllerId: formData.controllerId,
          regionId: editingOffice.isCentral ? undefined : formData.regionId
        };
        
        updateOfficeMutation.mutate({
          id: editingOffice._id,
          data: submitData
        });
      } else {
        // For creating new office
        const nextOfficeNumber = getNextOfficeNumber(formData.regionId);
        
        submitData = {
          officeNo: nextOfficeNumber,
          name: formData.name.trim(),
          controllerId: formData.controllerId,
          regionId: nextOfficeNumber === 1 ? undefined : formData.regionId
        };

        createOfficeMutation.mutate(submitData);
      }
    } catch (error: any) {
      let errorMessage = 'Unknown error occurred';
      
      if (error?.response?.status === 400) {
        // Business logic error
        errorMessage = error?.response?.data?.message || 'Invalid office data provided';
        toast.error(`❌ ${errorMessage}`);
      } else if (error?.response?.status === 404) {
        errorMessage = 'Office not found. It may have been deleted.';
        toast.error(`❌ ${errorMessage}`);
      } else if (error?.response?.status === 409) {
        errorMessage = 'Office number already exists. Please try a different number.';
        toast.error(`❌ ${errorMessage}`);
      } else if (error?.response?.status === 500) {
        errorMessage = 'Server error occurred. Please try again.';
        toast.error(`❌ ${errorMessage}`);
      } else {
        errorMessage = getErrorMessage(error, 'Failed to process office operation');
        toast.error(`❌ ${errorMessage}`);
      }
      
      console.error(`Office operation failed:`, error);
    }
  };

  const handleEdit = (office: Office) => {
    setEditingOffice(office);
    setFormData({
      name: office.name,
      controllerId: typeof office.controllerId === 'string' ? office.controllerId : office.controllerId._id,
      regionId: typeof office.regionId === 'string' ? office.regionId : office.regionId?._id || ''
    });
    setShowForm(true);
    setErrors({});
  };

  const handleDelete = (id: string) => {
    setDeleteOfficeId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (!deleteOfficeId) return;
    deleteOfficeMutation.mutate(deleteOfficeId);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteOfficeId(null);
  };

  const resetForm = () => {
    setFormData({ name: '', controllerId: '', regionId: '' });
    setEditingOffice(null);
    setShowForm(false);
    setErrors({});
    setControllerSearchTerm('');
    setShowControllerDropdown(false);
  };


  const filteredControllers = controllers.filter(controller =>
    controller.name.toLowerCase().includes(controllerSearchTerm.toLowerCase()) ||
    controller.cnic.includes(controllerSearchTerm) ||
    (controller.phone && controller.phone.includes(controllerSearchTerm))
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Manage Offices</h1>
          <button
            onClick={() => setShowCentralOfficeInfo(!showCentralOfficeInfo)}
            className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            title="Toggle Central Office System Information"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        <Button onClick={() => setShowForm(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Office
        </Button>
      </div>

      {/* Central Office Info - Conditional */}
      {showCentralOfficeInfo && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start">
            <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">Central Office System</h3>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Office #1 is automatically designated as the Central Office and cannot be deleted. 
                All other offices must be assigned to a specific region and will receive sequential office numbers within that region's range.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Building2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Offices</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{pagination?.total || offices.length}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <MapPin className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Central Office</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {offices.filter(office => office.isCentral).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <MapPin className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Regional Offices</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {offices.filter(office => !office.isCentral).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <UserCheck className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Assigned Controllers</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {new Set(offices.filter(office => office.controllerId).map(office => 
                  typeof office.controllerId === 'string' ? office.controllerId : office.controllerId._id
                )).size}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <Input
            type="text"
            placeholder="Search offices by name, number, controller, or region..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="card">
        {offices.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No offices found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Get started by adding your first office.
            </p>
          </div>
        ) : (
          <>
            <TableRoot>
              <Table>
                <THead>
                  <TR>
                    <TH>Office ID</TH>
                    <TH>Name</TH>
                    <TH>Controller</TH>
                    <TH>Region</TH>
                    <TH>Type</TH>
                    <TH>Created Date</TH>
                    <TH>Updated Date</TH>
                    <TH>Actions</TH>
                  </TR>
                </THead>
                <TBody>
                  {offices.map((office) => (
                    <TR key={office._id}>
                      <TD className="font-medium">{office.officeNo}</TD>
                      <TD>{office.name}</TD>
                      <TD>
                        {office.controllerId && typeof office.controllerId === 'object' && office.controllerId !== null && 'name' in office.controllerId
                          ? (office.controllerId as any).name
                          : 'Unassigned'}
                      </TD>
                      <TD>
                        {office.isCentral ? 'Central Office' : (
                          office.regionId && typeof office.regionId === 'object' && office.regionId !== null && 'name' in office.regionId
                            ? (office.regionId as any).name
                            : 'Unassigned'
                        )}
                      </TD>
                      <TD>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          office.isCentral 
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' 
                            : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        }`}>
                          {office.isCentral ? 'Central' : 'Regional'}
                        </span>
                      </TD>
                      <TD>{formatDate(office.createdAt || office.created_at)}</TD>
                      <TD>{formatDate(office.updatedAt || office.updated_at)}</TD>
                      <TD>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(office)}
                            className="flex items-center gap-1"
                          >
                            <Edit className="h-4 w-4" />
                            Edit
                          </Button>
                          {office.isCentral ? (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled
                              className="flex items-center gap-1 text-gray-400 cursor-not-allowed"
                              title="Central Office cannot be deleted"
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(office._id)}
                              className="flex items-center gap-1 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </Button>
                          )}
                        </div>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableRoot>
            
            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} offices
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={pagination.page === 1 || loading}
                  >
                    Previous
                  </Button>
                  <span className="flex items-center px-4 text-sm text-gray-700 dark:text-gray-300">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                    disabled={pagination.page === pagination.totalPages || loading}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {editingOffice ? 'Edit Office' : 'Add Office'}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={resetForm}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="form-label">Office Name</label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Enter office name"
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
              
              <div>
                <label className="form-label">Controller</label>
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Search controllers..."
                    value={controllerSearchTerm}
                    onChange={(e) => {
                      setControllerSearchTerm(e.target.value);
                      setShowControllerDropdown(true);
                    }}
                    onFocus={() => setShowControllerDropdown(true)}
                    className="mb-2"
                  />
                  
                  {/* Controller dropdown */}
                  {showControllerDropdown && (
                    <div className="absolute z-10 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {controllers
                        .filter(controller =>
                          controller.name.toLowerCase().includes(controllerSearchTerm.toLowerCase()) ||
                          controller.cnic.includes(controllerSearchTerm)
                        )
                        .map((controller) => (
                          <div
                            key={controller._id}
                            className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-b-0"
                            onMouseDown={(e) => {
                              e.preventDefault(); // Prevent input blur
                              e.stopPropagation(); // Prevent event bubbling
                              

                              
                              // Update state using the same approach as the Regions page
                              setFormData(prev => {
                                const newData = { ...prev, controllerId: controller._id };
                                return newData;
                              });
                              
                              setControllerSearchTerm(controller.name);
                              setShowControllerDropdown(false);
                            }}
                          >
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {controller.name} - {controller.cnic}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              Phone: {controller.phone}
                            </div>
                          </div>
                        ))}
                      {controllers.filter(controller =>
                        controller.name.toLowerCase().includes(controllerSearchTerm.toLowerCase()) ||
                        controller.cnic.includes(controllerSearchTerm)
                      ).length === 0 && (
                        <div className="px-3 py-2 text-gray-500 dark:text-gray-400">
                          {controllerSearchTerm ? 'No controllers found' : 'Type to search controllers...'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Current selection display */}
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Current Controller: <span className="font-medium text-gray-900 dark:text-gray-100">
                    {controllers.find(c => c._id === formData.controllerId)?.name || 'None'}
                  </span>
                </div>
                
                {errors.controllerId && <p className="text-red-500 text-xs mt-1">{errors.controllerId}</p>}
              </div>
              
              <div>
                <label className="form-label">Region</label>
                <select
                  value={formData.regionId}
                  onChange={(e) => setFormData({ ...formData, regionId: e.target.value })}
                  className={`form-select ${errors.regionId ? 'border-red-500' : ''}`}
                  disabled={editingOffice?.isCentral}
                >
                  <option value="">Select a region</option>
                  {regions.map((region) => {
                    const availableOffices = region.officeRange ? 
                      `${region.officeRange.start}-${region.officeRange.end}` : 'N/A';
                    return (
                      <option key={region._id} value={region._id}>
                        {region.name} (Range: {availableOffices})
                      </option>
                    );
                  })}
                </select>
                {editingOffice?.isCentral && (
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Central Office is not assigned to any region
                  </div>
                )}
                {formData.regionId && !editingOffice?.isCentral && (
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Next available office number: {getNextOfficeNumber(formData.regionId)}
                  </div>
                )}
                {errors.regionId && <p className="text-red-500 text-xs mt-1">{errors.regionId}</p>}
                <p className="text-xs text-gray-500 mt-1">
                  {editingOffice?.isCentral ? 
                    'Central Office is not assigned to any region' : 
                    'Regional offices must be assigned to a region'
                  }
                </p>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <Button type="submit" className="flex-1">
                  {editingOffice ? 'Update Office' : 'Add Office'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm} className="flex-1">
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Confirm Deletion</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to delete this office? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={cancelDelete} className="flex-1">
                Cancel
              </Button>
              <Button variant="error" onClick={confirmDelete} className="flex-1">
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageOffices;
